import { Link } from "react-router-dom";
import { Instagram, Facebook } from "lucide-react";
import { Monogram } from "./Header";
import { useSettings, siteText, DEFAULT_CONTACT, DEFAULT_HOURS } from "@/components/shared";

const LEGAL_LINKS = [
  { to: "/cartes-cadeaux", label: "Cartes cadeaux" },
  { to: "/fidelite", label: "Fidélité" },
  { to: "/espace", label: "Mon espace" },
  { to: "/mentions-legales", label: "Mentions légales" },
  { to: "/politique-de-confidentialite", label: "Confidentialité" },
  { to: "/cgv", label: "CGV" },
  { to: "/politique-de-cookies", label: "Cookies" },
  { to: "/gestion-des-cookies", label: "Gestion des cookies" },
];

export default function Footer() {
  const settings = useSettings();
  const contact = { ...DEFAULT_CONTACT, ...(settings?.contact || {}) };
  const hours = { ...DEFAULT_HOURS, ...(settings?.hours || {}) };

  return (
    <footer data-testid="site-footer" className="border-t border-white/10 bg-ink">
      <div className="mx-auto max-w-7xl px-6 py-16 md:px-12 md:py-20">
        <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-4">
          <div>
            <div className="flex items-center gap-3">
              <Monogram className="h-9 w-9" />
              <span className="text-sm font-bold tracking-[0.25em] text-ivory">PALAIS MAASAÏ</span>
            </div>
            <p className="mt-5 text-sm leading-relaxed text-ivory/45" data-testid="footer-signature">
              {siteText(settings, "signature") ||
                "Restaurant africain contemporain au cœur de Dijon."}
            </p>
            <div className="mt-6 flex items-center gap-3">
              <a
                href={settings?.instagram_url || "https://instagram.com"}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                data-testid="footer-instagram"
                className="flex h-9 w-9 items-center justify-center rounded-md border border-white/10 text-ivory/50 transition-colors hover:border-gold hover:text-gold"
              >
                <Instagram className="h-4 w-4" strokeWidth={1.5} />
              </a>
              <a
                href={settings?.facebook_url || "https://facebook.com"}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                data-testid="footer-facebook"
                className="flex h-9 w-9 items-center justify-center rounded-md border border-white/10 text-ivory/50 transition-colors hover:border-gold hover:text-gold"
              >
                <Facebook className="h-4 w-4" strokeWidth={1.5} />
              </a>
            </div>
          </div>

          <div data-testid="footer-address">
            <h3 className="text-xs font-semibold uppercase tracking-[0.25em] text-gold">Adresse</h3>
            <p className="mt-4 text-sm leading-relaxed text-ivory/55">
              {contact.address_name}
              <br />
              {contact.address_street}
              <br />
              {contact.address_city}
            </p>
          </div>

          <div data-testid="footer-contact">
            <h3 className="text-xs font-semibold uppercase tracking-[0.25em] text-gold">Contact</h3>
            <p className="mt-4 text-sm leading-relaxed text-ivory/55">
              <a
                href={`https://wa.me/${contact.whatsapp}`}
                target="_blank"
                rel="noopener noreferrer"
                className="transition-colors hover:text-gold"
              >
                WhatsApp : {contact.phone_display}
              </a>
              <br />
              <a href={`mailto:${contact.email}`} className="transition-colors hover:text-gold">
                {contact.email}
              </a>
            </p>
          </div>

          <div data-testid="footer-hours">
            <h3 className="text-xs font-semibold uppercase tracking-[0.25em] text-gold">Horaires</h3>
            <p className="mt-4 text-sm leading-relaxed text-ivory/55">
              {hours.week_label}
              <br />
              {hours.week_1}
              {hours.week_2 ? ` · ${hours.week_2}` : ""}
            </p>
            <p className="mt-3 text-sm leading-relaxed text-ivory/55">
              {hours.weekend_label}
              <br />
              {hours.weekend_1}
            </p>
          </div>
        </div>

        <div className="mt-16 flex flex-col items-center gap-6 border-t border-white/5 pt-8 text-center">
          <nav className="flex flex-wrap justify-center gap-x-6 gap-y-2" data-testid="footer-legal-links">
            {LEGAL_LINKS.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="text-xs text-ivory/40 transition-colors hover:text-gold"
              >
                {link.label}
              </Link>
            ))}
          </nav>
          <p className="text-[11px] tracking-wide text-ivory/30" data-testid="footer-copyright">
            © 2026 Palais Maasaï – Tous droits réservés
          </p>
        </div>
      </div>
    </footer>
  );
}
